import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Button, Modal, Form, Table, Badge, Alert, Row, Col, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { usePermissions, USER_ROLES, MODULES, ACTIONS } from "../../hooks/usePermissions";
import UsersService from "../../services/api/users";
import TeamService from "../../services/api/teamService";
import PermissionGroupsService from "../../services/api/permissionGroupsService";
import ProductsService from "../../services/api/products";
import PhoneValidator from "../../utils/phoneValidation";
import CreateUserModal from "../team/CreateUserModal";

const TeamManagement = () => {
  const { hasPermission, isOwner, getUserRole, isSeller } = usePermissions();
  
  // Estados
  const [users, setUsers] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: USER_ROLES.EMPLOYEE,
    status: "active",
    permissions: {},
    permission_group_id: null // Novo campo para grupo
  });
  
  // Estados dos filtros
  const [filters, setFilters] = useState({
    type: "all", // 'all', 'colaboradores', 'vendedores', 'afiliados'
    search: "",
    role: "",
    status: ""
  });

  // Carregar membros da equipe (novo sistema)
  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await TeamService.getTeamMembers({
        type: filters.type,
        search: filters.search,
        status: filters.status
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar membros da equipe:', error);
      // Fallback para sistema antigo
      try {
        const fallbackResponse = await UsersService.getUsers();
        setUsers(fallbackResponse.data || []);
      } catch (fallbackError) {
        toast.error('Erro ao carregar usuários');
      }
    } finally {
      setLoading(false);
    }
  };

  // Carregar grupos de permissões
  const loadPermissionGroups = async () => {
    try {
      const response = await PermissionGroupsService.getGroups();
      setPermissionGroups(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  // Carregar estatísticas da equipe
  const loadTeamStats = async () => {
    try {
      const response = await TeamService.getStats();
      setTeamStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Carregar produtos
  const loadProducts = async () => {
    try {
      const response = await ProductsService.getProducts();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  useEffect(() => {
    if (isOwner()) {
      loadTeamMembers();
      loadPermissionGroups();
      loadTeamStats();
      loadProducts();
    }
  }, []);

  // Recarregar quando filtros mudarem
  useEffect(() => {
    if (isOwner()) {
      loadTeamMembers();
    }
  }, [filters.type, filters.status]);

  // Abrir modal para criar/editar usuário
  const handleOpenModal = (user = null) => {
    setEditingUser(user);
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setShowPermissionsModal(false);
    setEditingUser(null);
  };

  // Salvar usuário (integrado com novo sistema de tipos)
  const handleSaveUser = async (userData) => {
    try {
      setLoading(true);

      const { userType, ...formData } = userData;

      // Criar baseado no tipo
      if (userType === 'collaborator') {
        // Colaborador
        const collaboratorData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          permission_group_id: formData.permission_group_id
        };
        
        // Converter permission_group_id para número
        if (collaboratorData.permission_group_id) {
          collaboratorData.permission_group_id = parseInt(collaboratorData.permission_group_id, 10);
        }
        
        // Log para debug
        console.log('📝 Dados do colaborador sendo enviados:', collaboratorData);
        console.log('📝 permission_group_id:', collaboratorData.permission_group_id, 'Tipo:', typeof collaboratorData.permission_group_id);
        
        await TeamService.createCollaborator(collaboratorData);
        toast.success('Colaborador criado com sucesso!');
      } else if (userType === 'seller') {
        // Vendedor
        await TeamService.createSeller({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password
        });
        toast.success('Vendedor criado e vinculado aos produtos!');
      } else if (userType === 'affiliate') {
        // Afiliado Direto
        await TeamService.createAffiliate({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          products: formData.selectedProducts.map(id => ({
            productId: id,
            commissionRate: 25.0 // Comissão padrão
          }))
        });
        toast.success('Afiliado direto criado e vinculado aos produtos!');
      }

      loadTeamMembers();
      loadTeamStats();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao salvar usuário';
      console.error('📋 Mensagem de erro do backend:', errorMessage);
      console.error('📋 Dados completos da resposta:', error.response?.data);
      toast.error(errorMessage);
      throw error; // Re-lançar para o modal tratar
    } finally {
      setLoading(false);
    }
  };

  // Deletar usuário
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja deletar este usuário?')) {
      return;
    }

    try {
      setLoading(true);
      await UsersService.deleteUser(userId);
      toast.success('Usuário deletado com sucesso!');
      loadTeamMembers();
      loadTeamStats();
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      toast.error(error.response?.data?.message || 'Erro ao deletar usuário');
    } finally {
      setLoading(false);
    }
  };

  // Gerar senha aleatória
  const handleGeneratePassword = async () => {
    try {
      const response = await UsersService.generatePassword();
      setFormData(prev => ({ ...prev, password: response.password }));
      toast.success('Senha gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar senha:', error);
      toast.error('Erro ao gerar senha');
    }
  };

  // Formatação de telefone
  const handlePhoneChange = (e) => {
    const formatted = PhoneValidator.formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  // Identificar tipo do usuário (usa user_type do backend quando disponível)
  const getUserType = (user) => {
    // Priorizar user_type do backend se existir
    if (user.user_type) {
      return user.user_type; // 'vendedor', 'afiliado_direto', 'colaborador', 'outro'
    }
    
    // Fallback para lógica anterior (compatibilidade)
    if (user.seller_exclusive_owner_id) return 'vendedor';
    if (user.role === 'colaborador' || user.role === USER_ROLES.EMPLOYEE) return 'colaborador';
    if (user.type === 'direct_affiliate' || user.is_direct_affiliate) return 'afiliado_direto';
    return 'outro';
  };

  // Badges de identificação visual
  const getUserTypeBadge = (user) => {
    const type = getUserType(user);
    
    if (type === 'vendedor') {
      return <Badge bg="success" className="me-1">🟢 Vendedor</Badge>;
    }
    
    if (type === 'afiliado_direto') {
      return <Badge bg="primary" className="me-1">🔵 Meu Afiliado</Badge>;
    }
    
    if (type === 'colaborador') {
      return <Badge bg="secondary" className="me-1">👤 Colaborador</Badge>;
    }
    
    return null;
  };

  // Obter permissões padrão por role
  const getDefaultPermissionsForRole = (role) => {
    const defaultPermissions = {
      [MODULES.PRODUTOS]: { [ACTIONS.VIEW]: false, [ACTIONS.CREATE]: false, [ACTIONS.EDIT]: false, [ACTIONS.DELETE]: false },
      [MODULES.FINANCEIRO]: { [ACTIONS.VIEW]: false, [ACTIONS.MANAGE]: false },
      [MODULES.RELATORIOS]: { [ACTIONS.VIEW]: false, [ACTIONS.EXPORT]: false },
      [MODULES.VENDAS]: { [ACTIONS.VIEW]: false, [ACTIONS.MANAGE]: false },
      [MODULES.AFILIADOS]: { [ACTIONS.VIEW]: false, [ACTIONS.MANAGE]: false },
      [MODULES.USUARIOS]: { [ACTIONS.VIEW]: false, [ACTIONS.CREATE]: false, [ACTIONS.EDIT]: false, [ACTIONS.DELETE]: false }
    };

    // Definir permissões baseadas no role
    switch (role) {
      case USER_ROLES.MANAGER:
        return {
          [MODULES.PRODUTOS]: { [ACTIONS.VIEW]: true, [ACTIONS.CREATE]: true, [ACTIONS.EDIT]: true, [ACTIONS.DELETE]: false },
          [MODULES.FINANCEIRO]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: false },
          [MODULES.RELATORIOS]: { [ACTIONS.VIEW]: true, [ACTIONS.EXPORT]: true },
          [MODULES.VENDAS]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: true },
          [MODULES.AFILIADOS]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: true },
          [MODULES.USUARIOS]: { [ACTIONS.VIEW]: true, [ACTIONS.CREATE]: false, [ACTIONS.EDIT]: false, [ACTIONS.DELETE]: false }
        };
      case USER_ROLES.COPRODUCER:
        return {
          [MODULES.PRODUTOS]: { [ACTIONS.VIEW]: true, [ACTIONS.CREATE]: true, [ACTIONS.EDIT]: true, [ACTIONS.DELETE]: false },
          [MODULES.FINANCEIRO]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: false },
          [MODULES.RELATORIOS]: { [ACTIONS.VIEW]: true, [ACTIONS.EXPORT]: false },
          [MODULES.VENDAS]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: false },
          [MODULES.AFILIADOS]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: false },
          [MODULES.USUARIOS]: { [ACTIONS.VIEW]: false, [ACTIONS.CREATE]: false, [ACTIONS.EDIT]: false, [ACTIONS.DELETE]: false }
        };
      default:
        return defaultPermissions;
    }
  };

  // Atualizar permissões quando role muda
  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setFormData(prev => ({
      ...prev,
      role: newRole,
      permissions: getDefaultPermissionsForRole(newRole)
    }));
  };

  // Atualizar permissão específica
  const handlePermissionChange = (module, action, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [action]: value
        }
      }
    }));
  };

  // Filtrar usuários (filtro local adicional)
  const filteredUsers = users.filter(user => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesSearch = 
        user.name?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    if (filters.role && user.role !== filters.role) return false;
    
    return true;
  });

  // Função para obter label do role (com lógica especial para sellers)
  const getRoleLabel = (user) => {
    if (user.role === USER_ROLES.SELLER) {
      // Seller pode ser vendedor ou afiliado direto
      const type = getUserType(user);
      return type === 'vendedor' ? 'Vendedor' : 'Afiliado Direto';
    }
    
    const roleLabelsMap = {
      [USER_ROLES.OWNER]: 'Proprietário',
      [USER_ROLES.ADMIN]: 'Administrador',
      [USER_ROLES.MANAGER]: 'Gerente',
      [USER_ROLES.EMPLOYEE]: 'Funcionário',
      [USER_ROLES.COPRODUCER]: 'Coprodutor'
    };
    
    return roleLabelsMap[user.role] || user.role;
  };

  // Cores dos badges por role
  const roleBadgeColors = {
    [USER_ROLES.OWNER]: 'danger',
    [USER_ROLES.ADMIN]: 'warning',
    [USER_ROLES.MANAGER]: 'info',
    [USER_ROLES.EMPLOYEE]: 'secondary',
    [USER_ROLES.COPRODUCER]: 'success',
    [USER_ROLES.SELLER]: 'primary' // Sellers podem ser vendedores ou afiliados
  };


  return (
    <div className="container-fluid">
      {/* Estatísticas */}
      {teamStats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="p-3 rounded-circle bg-primary bg-opacity-10 me-3">
                    <Icon icon="mdi:account-group" className="fs-4 text-primary" />
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6c757d', display: 'block' }}>Total de Membros</span>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', display: 'block' }}>{teamStats.totalTeamMembers || users.length}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="p-3 rounded-circle bg-secondary bg-opacity-10 me-3">
                    <Icon icon="mdi:account-cog" className="fs-4 text-secondary" />
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6c757d', display: 'block' }}>Colaboradores</span>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', display: 'block' }}>{teamStats.totalCollaborators || 0}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="p-3 rounded-circle bg-success bg-opacity-10 me-3">
                    <Icon icon="mdi:cart" className="fs-4 text-success" />
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6c757d', display: 'block' }}>Vendedores</span>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', display: 'block' }}>{teamStats.totalSellers || 0}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="p-3 rounded-circle bg-info bg-opacity-10 me-3">
                    <Icon icon="mdi:handshake" className="fs-4 text-info" />
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6c757d', display: 'block' }}>Meus Afiliados</span>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', display: 'block' }}>{teamStats.totalDirectAffiliates || 0}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtros */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <span style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Icon icon="mdi:filter-outline" /> Filtros
          </span>
          <Row className="g-3">
            <Col md={3}>
              <Form.Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="all">Todos os tipos</option>
                <option value="colaboradores">👤 Colaboradores</option>
                <option value="vendedores">🟢 Vendedores</option>
                <option value="afiliados">🔵 Meus Afiliados</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="🔍 Buscar por nome ou email..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.role}
                onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="">Todos os perfis</option>
                <option value={USER_ROLES.OWNER}>Proprietário</option>
                <option value={USER_ROLES.ADMIN}>Administrador</option>
                <option value={USER_ROLES.MANAGER}>Gerente</option>
                <option value={USER_ROLES.EMPLOYEE}>Colaborador</option>
                <option value={USER_ROLES.COPRODUCER}>Coprodutor</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Todos os status</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de usuários */}
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-0">
              <Icon icon="mdi:account-group-outline" /> 
              Usuários ({filteredUsers.length})
            </h6>
            {hasPermission(MODULES.USUARIOS, ACTIONS.CREATE) && (
              <Button
                variant="primary"
                className="d-flex align-items-center gap-2"
                onClick={() => handleOpenModal()}
                disabled={loading}
              >
                <Icon icon="mdi:plus" /> Novo Usuário
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Icon icon="mdi:loading" className="fs-2 spinner-border" />
              <p className="mt-2">Carregando usuários...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Nome/Email</th>
                    <th>Tipo</th>
                    <th>Grupo</th>
                    <th>Perfil</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        <Icon icon="mdi:account-off" className="fs-1 mb-2 d-block" />
                        Nenhum usuário encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div>
                            <strong>{user.name}</strong>
                            <br />
                            <small className="text-muted">{user.email}</small>
                          </div>
                        </td>
                        <td>
                          {getUserTypeBadge(user)}
                        </td>
                        <td>
                          {getUserType(user) === 'colaborador' && user.permission_group_name ? (
                            <span style={{ fontSize: '13px' }}>{user.permission_group_name}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={roleBadgeColors[user.role] || 'secondary'}>
                            {getRoleLabel(user)}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={user.status === 'active' ? 'success' : 'secondary'}>
                            {user.status === 'active' ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td>
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '—'}
                        </td>
                        <td>
                          <div className="d-flex gap-1 justify-content-center">
                            {hasPermission(MODULES.USUARIOS, ACTIONS.EDIT) && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleOpenModal(user)}
                                disabled={loading}
                                title="Editar"
                              >
                                <Icon icon="mdi:pencil" />
                              </Button>
                            )}
                            {hasPermission(MODULES.USUARIOS, ACTIONS.DELETE) && 
                             user.role !== USER_ROLES.OWNER && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={loading}
                                title="Deletar"
                              >
                                <Icon icon="mdi:delete" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal Unificado de Criar/Editar Usuário */}
      <CreateUserModal
        show={showModal}
        onHide={handleCloseModal}
        onSave={handleSaveUser}
        editingUser={editingUser}
        permissionGroups={permissionGroups}
        products={products}
      />

      {/* Modal de Permissões */}
      <Modal show={showPermissionsModal} onHide={() => setShowPermissionsModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Configurar Permissões</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formData.role === USER_ROLES.OWNER ? (
            <Alert variant="warning">
              <Icon icon="mdi:crown" className="me-2" />
              Proprietários têm acesso total a todas as funcionalidades automaticamente.
            </Alert>
          ) : (
            <div>
              {Object.entries(formData.permissions).map(([module, permissions]) => (
                <Card key={module} className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0 text-capitalize">{module}</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {Object.entries(permissions).map(([action, value]) => (
                        <Col md={6} key={action} className="mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`${module}-${action}`}
                            label={action === 'view' ? 'Visualizar' : 
                                   action === 'create' ? 'Criar' :
                                   action === 'edit' ? 'Editar' :
                                   action === 'delete' ? 'Deletar' :
                                   action === 'manage' ? 'Gerenciar' :
                                   action === 'export' ? 'Exportar' : action}
                            checked={value}
                            onChange={(e) => handlePermissionChange(module, action, e.target.checked)}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowPermissionsModal(false)}>
            Salvar Permissões
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeamManagement;























